import { useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAgentBridge } from "@/lib/agent/worker-bridge";
import { useAgentStore } from "@/stores/useAgentStore";
import { usePanelStore } from "@/stores/usePanelStore";
import { useAuth } from "@/hooks/useAuth";
import * as runStore from "@/lib/agent/storage/run-store";
import * as sessionStore from "@/lib/agent/storage/session-store";
import * as panelStorage from "@/lib/agent/storage/panel-storage";
import type { AgentConfig, AgentEvent, AgentRun, LLMMessage } from "@/lib/agent/types";
import { DEFAULT_AGENT_CONFIG } from "@/lib/agent/types";

/** Generate a short title from the first user prompt */
function generateTitle(prompt: string): string {
  const cleaned = prompt.replace(/\s+/g, " ").trim();
  if (cleaned.length <= 60) return cleaned;
  return cleaned.slice(0, 57) + "...";
}

/**
 * Hook for managing agent runs with multi-turn conversation support.
 */
export function useAgentRun() {
  const queryClient = useQueryClient();
  const store = useAgentStore();
  const { userId } = useAuth();
  const bridgeRef = useRef(getAgentBridge());
  const configRef = useRef<AgentConfig | null>(null);

  // Run history query — scoped by userId to prevent cross-user cache hits
  const historyQuery = useQuery({
    queryKey: ["agent-runs", userId],
    queryFn: () => runStore.listRuns(),
    staleTime: 5_000,
    enabled: !!userId,
  });

  /** Snapshot the current conversation (history + in-progress turn) and persist */
  const persistConversation = useCallback(
    (runId: string) => {
      const { conversationHistory, streamedText, events } =
        useAgentStore.getState();
      const turns = [...conversationHistory];
      if (streamedText || events.length > 0) {
        turns.push({
          role: "assistant",
          text: streamedText,
          toolEvents: events,
        });
      }
      if (turns.length > 0) {
        sessionStore.saveConversation(runId, turns);
      }
    },
    [],
  );

  // Event handler that updates both Zustand and IndexedDB
  const handleEvent = useCallback(
    (event: AgentEvent) => {
      store.pushEvent(event);

      // Persist status changes to IndexedDB
      if (event.type === "status_change" && store.activeRunId) {
        runStore.updateRunStatus(store.activeRunId, event.status);
      }
      if (event.type === "step_end" && store.activeRunId) {
        runStore.updateRunStatus(store.activeRunId, "executing", {
          tokensUsed: event.tokensUsed,
        } as Partial<AgentRun>);
      }
      // Bridge human_task_detected events into tile system
      if (event.type === "human_task_detected" && Array.isArray(event.tasks) && event.tasks.length > 0) {
        const runId = useAgentStore.getState().activeRunId;
        console.log("[useAgentRun] human_task_detected:", event.tasks.length, "tasks, runId:", runId);
        if (runId) {
          const now = new Date().toISOString();
          const savePromises = event.tasks.map((task, i) => {
            const tile = {
              tileId: `ht-${runId}-${Date.now()}-${i}`,
              runId,
              type: "human_task" as const,
              title: task.title,
              content: JSON.stringify({
                title: task.title,
                description: task.description,
                status: "pending",
                skills: task.skills,
                estimatedHours: task.estimatedHours,
                budgetUsd: task.budgetUsd,
              }),
              createdAt: now,
              updatedAt: now,
            };
            return panelStorage.saveWidget(tile);
          });
          Promise.all(savePromises).then(() => {
            panelStorage.getWidgetsByRun(runId).then((tiles) => {
              console.log("[useAgentRun] tiles after human_task_detected save:", tiles.length, "types:", tiles.map((t) => t.type));
              usePanelStore.getState().setTiles(tiles);
              if (!usePanelStore.getState().isOpen) {
                usePanelStore.getState().revealPanel({ markUnseen: true });
              }
            });
          });
        }
      }

      // Handle tile tool results: update panel store from IndexedDB
      if (event.type === "tool_call_result") {
        console.log("[useAgentRun] tool_call_result:", event.toolName, "is_error:", event.result.is_error);
        const tileTools = ["create_tile", "update_tile", "delete_tile"];
        if (tileTools.includes(event.toolName) && !event.result.is_error) {
          try {
            const parsed = JSON.parse(event.result.content);
            console.log("[useAgentRun] parsed tile result:", parsed._tileAction, "tileId:", parsed.tileId, "type:", parsed.type);
            if (parsed._tileAction) {
              const runId = useAgentStore.getState().activeRunId;
              console.log("[useAgentRun] activeRunId:", runId);
              if (parsed._tileAction === "deleted") {
                usePanelStore.getState().removeTile(parsed.tileId);
              } else if (runId) {
                // Refresh tiles from IndexedDB for created/updated
                panelStorage.getWidgetsByRun(runId).then((tiles) => {
                  console.log("[useAgentRun] refreshed tiles from IndexedDB:", tiles.length, "types:", tiles.map((t) => t.type));
                  usePanelStore.getState().setTiles(tiles);
                  if (parsed._tileAction === "created" && !usePanelStore.getState().isOpen) {
                    console.log("[useAgentRun] auto-opening panel");
                    usePanelStore.getState().revealPanel({ markUnseen: true });
                  }
                });
              }
            }
          } catch {
            // Not a tile result JSON
          }
        }

        // Handle view tool results: refresh views from IndexedDB
        const viewTools = ["create_view", "update_view", "delete_view", "set_active_view"];
        if (viewTools.includes(event.toolName) && !event.result.is_error) {
          try {
            const parsed = JSON.parse(event.result.content);
            if (parsed._viewAction) {
              const runId = useAgentStore.getState().activeRunId;
              if (runId) {
                panelStorage.getViewsByRun(runId).then((views) => {
                  usePanelStore.getState().setViews(views);
                  if (parsed._viewAction === "created" && !usePanelStore.getState().isOpen) {
                    usePanelStore.getState().revealPanel({ markUnseen: true });
                  }
                  if (parsed._viewAction === "activated") {
                    usePanelStore.getState().setActiveViewId(parsed.viewId);
                    if (!usePanelStore.getState().isOpen) {
                      usePanelStore.getState().revealPanel({ markUnseen: true });
                    }
                  }
                });
              }
            }
          } catch {
            // Not a view result JSON
          }
        }

        // Handle file tool results: bump file version to trigger FilesView refresh
        const fileTools = ["write_file", "read_file", "list_files", "generate_document"];
        if (fileTools.includes(event.toolName) && !event.result.is_error) {
          usePanelStore.getState().bumpFileVersion();
        }
      }

      if (event.type === "run_done" || event.type === "run_error") {
        // Persist conversation after Zustand has been updated by pushEvent above
        const runId = useAgentStore.getState().activeRunId;
        if (runId) {
          // Use queueMicrotask so the store state settles before we snapshot
          queueMicrotask(() => persistConversation(runId));
        }
        queryClient.invalidateQueries({ queryKey: ["agent-runs", userId] });
      }
    },
    [store, queryClient, persistConversation, userId],
  );

  // Start a new run or continue an existing conversation
  const startMutation = useMutation({
    mutationFn: async ({ prompt }: { prompt: string }) => {
      console.log("[useAgentRun] mutationFn called", { prompt: prompt.slice(0, 50) });
      const config: AgentConfig = { ...DEFAULT_AGENT_CONFIG };
      configRef.current = config;

      const existingRunId = store.activeRunId;
      const canContinue =
        existingRunId &&
        (store.status === "done" || store.status === "error");

      console.log("[useAgentRun] canContinue?", { existingRunId, status: store.status, canContinue });

      if (canContinue) {
        // Continue the existing conversation
        console.log("[useAgentRun] continuing existing run", existingRunId);
        store.continueTurn(prompt);
        try {
          await bridgeRef.current.continueRun(
            existingRunId,
            config,
            prompt,
            handleEvent,
          );
        } catch {
          // Worker lost in-memory conversation (e.g. page reload / session switch).
          // Rebuild from persisted messages and resume instead.
          console.log("[useAgentRun] continueRun failed, falling back to resumeRun");
          const messages = await runStore.getMessages(existingRunId);
          const llmMessages: LLMMessage[] = messages.map((m) => ({
            role: m.role === "user" || m.role === "tool_result" ? "user" as const : "assistant" as const,
            content: m.content,
          }));
          llmMessages.push({ role: "user", content: [{ type: "text" as const, text: prompt }] });
          await bridgeRef.current.resumeRun(
            existingRunId,
            config,
            llmMessages,
            handleEvent,
          );
        }
        return existingRunId;
      }

      // Start a brand new run
      const runId = crypto.randomUUID();
      const run: AgentRun = {
        id: runId,
        status: "planning",
        prompt,
        title: generateTitle(prompt),
        model: config.model,
        maxIterations: config.maxIterations,
        maxTokens: config.maxTokens,
        tokensUsed: 0,
        questId: null,
        error: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      console.log("[useAgentRun] saving run to IndexedDB...");
      await runStore.saveRun(run);
      console.log("[useAgentRun] run saved, starting UI state...");

      // Update UI state (resets history for fresh conversation)
      store.startRun(runId);

      // Add user message to history after startRun reset
      store.continueTurn(prompt);

      // Start the agent in the Worker (pass runId so worker uses the same ID)
      console.log("[useAgentRun] calling bridgeRef.current.startRun...");
      await bridgeRef.current.startRun(runId, config, prompt, handleEvent);
      console.log("[useAgentRun] startRun resolved");

      return runId;
    },
    onError: (error) => {
      console.error("[useAgentRun] mutation onError:", error);
      store.pushEvent({
        type: "run_error",
        error: error instanceof Error ? error.message : String(error),
      });
      store.pushEvent({ type: "status_change", status: "error" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-runs", userId] });
    },
  });

  // Cancel active run
  const cancel = useCallback(async () => {
    if (store.activeRunId) {
      await bridgeRef.current.cancelRun(store.activeRunId);
      await runStore.updateRunStatus(store.activeRunId, "error", {
        error: "Cancelled by user",
      } as Partial<AgentRun>);
      store.reset();
      queryClient.invalidateQueries({ queryKey: ["agent-runs", userId] });
    }
  }, [store, queryClient, userId]);

  // Send clarification response
  const sendClarification = useCallback(
    async (response: string) => {
      if (!store.activeRunId || !configRef.current) return;

      // Get existing messages from the run and append user response
      const messages = await runStore.getMessages(store.activeRunId);
      const llmMessages = messages.map((m) => ({
        role: m.role === "user" || m.role === "tool_result" ? "user" as const : "assistant" as const,
        content: m.content,
      }));

      // Append clarification response
      llmMessages.push({ role: "user", content: [{ type: "text" as const, text: response }] });

      // Add the user's reply to the visible conversation history
      store.continueTurn(response);

      await bridgeRef.current.resumeRun(
        store.activeRunId,
        configRef.current,
        llmMessages,
        handleEvent,
      );
    },
    [store, handleEvent],
  );

  // Save current conversation before starting new chat
  const newChat = useCallback(async () => {
    if (store.activeRunId) {
      persistConversation(store.activeRunId);
    }
    store.newChat();
    usePanelStore.getState().resetPanel();
  }, [store, persistConversation]);

  // Load a previous session
  const loadSession = useCallback(
    async (runId: string) => {
      // Save current session first
      if (store.activeRunId && store.activeRunId !== runId) {
        persistConversation(store.activeRunId);
      }

      // Load the target session
      const run = await runStore.getRun(runId);
      if (!run) return;

      const turns = await sessionStore.getConversation(runId);

      // Fallback for old sessions with no conversation data:
      // show the original prompt so the user sees something
      const fallbackTurns = turns ?? [
        { role: "user" as const, text: run.prompt, toolEvents: [] },
      ];

      const loadedStatus =
        run.status === "done" || run.status === "error" ? run.status : "done";
      store.loadSession(runId, fallbackTurns, loadedStatus as "done" | "error");
    },
    [store, persistConversation],
  );

  // Delete a session
  const deleteSession = useCallback(
    async (runId: string) => {
      // If deleting the active session, reset to new chat
      if (store.activeRunId === runId) {
        store.newChat();
      }
      await runStore.deleteRun(runId);
      queryClient.invalidateQueries({ queryKey: ["agent-runs", userId] });
    },
    [store, queryClient, userId],
  );

  return {
    // State
    status: store.status,
    activeRunId: store.activeRunId,
    conversationHistory: store.conversationHistory,
    events: store.events,
    streamedText: store.streamedText,
    tokensUsed: store.tokensUsed,
    iteration: store.iteration,
    clarificationQuestion: store.clarificationQuestion,
    clarificationOptions: store.clarificationOptions,
    humanTasks: store.humanTasks,
    humanTasksTemplate: store.humanTasksTemplate,
    humanTasksBrief: store.humanTasksBrief,
    fundWalletRequest: store.fundWalletRequest,

    // Actions
    startRun: startMutation.mutate,
    isStarting: startMutation.isPending,
    cancel,
    sendClarification,
    newChat,
    loadSession,
    deleteSession,

    // History
    runs: historyQuery.data ?? [],
    isLoadingHistory: historyQuery.isPending,
  };
}
